import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { Container, Row, Col } from "react-bootstrap";
import { db } from "../firebase";

import Header from "../components/Header";
import CustomerForm from "../components/CustomerForm";
import CustomerTable from "../components/CustomerTable";

function ManageCustomers() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+91");
  const [plan, setPlan] = useState("1 Month");
  const [status, setStatus] = useState("Active");
  const [expiryDate, setExpiryDate] = useState("");

  const [callAccess, setCallAccess] = useState([
    "Long Term",
    "Mid Term",
    "Short Term",
  ]);

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");

  const [editingCustomerId, setEditingCustomerId] = useState(null);

  // Keeps the customer's original joining time while editing.
  const [editingCustomerCreatedAt, setEditingCustomerCreatedAt] =
    useState(null);

  const normalizeEmail = (value) => {
    return String(value || "").trim().toLowerCase();
  };

  const formatPhone = (value) => {
    let cleaned = String(value || "").replace(/\s/g, "");

    if (!cleaned.startsWith("+91")) {
      cleaned = "+91" + cleaned.replace("+91", "");
    }

    return cleaned;
  };

  const loadCustomers = async () => {
    try {
      const data = await getDocs(collection(db, "customers"));

      const list = data.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));

      list.sort((a, b) => {
        if (a.status === "Active" && b.status !== "Active") {
          return -1;
        }

        if (a.status !== "Active" && b.status === "Active") {
          return 1;
        }

        return a.name?.localeCompare(b.name || "") || 0;
      });

      setCustomers(list);
    } catch (error) {
      console.error("Unable to load customers:", error);
      alert("Unable to load customers.");
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const calculateExpiry = (selectedPlan) => {
    const date = new Date();

    if (selectedPlan === "1 Month") {
      date.setMonth(date.getMonth() + 1);
    }

    if (selectedPlan === "3 Month") {
      date.setMonth(date.getMonth() + 3);
    }

    if (selectedPlan === "6 Month") {
      date.setMonth(date.getMonth() + 6);
    }

    if (selectedPlan === "1 Year") {
      date.setFullYear(date.getFullYear() + 1);
    }

    return date.toISOString().split("T")[0];
  };

  const clearForm = () => {
    setName("");
    setEmail("");
    setPhone("+91");
    setPlan("1 Month");
    setStatus("Active");
    setExpiryDate("");

    setCallAccess([
      "Long Term",
      "Mid Term",
      "Short Term",
    ]);

    setEditingCustomerId(null);
    setEditingCustomerCreatedAt(null);
  };

  const addCustomer = async () => {
    try {
      if (!name || !email) {
        alert("Please fill customer name and email.");
        return;
      }

      if (callAccess.length === 0) {
        alert("Please select at least one call access type.");
        return;
      }

      const cleanEmail = normalizeEmail(email);
      const cleanPhone = formatPhone(phone);
      const expiry = expiryDate || calculateExpiry(plan);
      const currentTimestamp = Date.now();

      await setDoc(doc(db, "customers", cleanEmail), {
        name: name.trim(),
        email: cleanEmail,
        phone: cleanPhone,
        plan,
        status,
        expiryDate: expiry,
        callAccess,

        // This is the customer's joining time.
        // It is used to prevent new customers from seeing old calls.
        createdAt: currentTimestamp,
        joinedAt: currentTimestamp,

        updatedAt: currentTimestamp,
      });

      alert("✅ Customer Added Successfully");

      clearForm();
      await loadCustomers();
    } catch (error) {
      console.error("Unable to add customer:", error);
      alert("Unable to add customer.");
    }
  };

  const startEditCustomer = (customer) => {
    setEditingCustomerId(customer.id);

    // Preserve the customer's original joining date.
    setEditingCustomerCreatedAt(
      customer.createdAt ||
        customer.joinedAt ||
        customer.joinDate ||
        null
    );

    setName(customer.name || "");
    setEmail(customer.email || customer.id || "");
    setPhone(customer.phone || "+91");
    setPlan(customer.plan || "1 Month");
    setStatus(customer.status || "Active");
    setExpiryDate(customer.expiryDate || "");

    setCallAccess(
      customer.callAccess || [
        "Long Term",
        "Mid Term",
        "Short Term",
      ]
    );
  };

  const updateCustomer = async () => {
    try {
      if (!editingCustomerId) {
        alert("No customer selected for editing.");
        return;
      }

      if (!name || !email || !plan || !status || !expiryDate) {
        alert("Please fill all fields.");
        return;
      }

      if (callAccess.length === 0) {
        alert("Please select at least one call access type.");
        return;
      }

      const cleanEmail = normalizeEmail(email);
      const cleanPhone = formatPhone(phone);
      const currentTimestamp = Date.now();

      const customerData = {
        name: name.trim(),
        email: cleanEmail,
        phone: cleanPhone,
        plan,
        status,
        expiryDate,
        callAccess,

        // Keep original joining time while editing.
        ...(editingCustomerCreatedAt
          ? {
              createdAt: editingCustomerCreatedAt,
              joinedAt: editingCustomerCreatedAt,
            }
          : {}),

        updatedAt: currentTimestamp,
      };

      if (editingCustomerId !== cleanEmail) {
        await setDoc(
          doc(db, "customers", cleanEmail),
          customerData
        );

        await deleteDoc(
          doc(db, "customers", editingCustomerId)
        );
      } else {
        await updateDoc(
          doc(db, "customers", editingCustomerId),
          customerData
        );
      }

      alert("✅ Customer Updated Successfully");

      clearForm();
      await loadCustomers();
    } catch (error) {
      console.error("Unable to update customer:", error);
      alert("Unable to update customer.");
    }
  };

  const deleteCustomer = async (id) => {
    try {
      const confirmDelete = window.confirm(
        "Are you sure you want to delete this customer?"
      );

      if (!confirmDelete) {
        return;
      }

      await deleteDoc(doc(db, "customers", id));

      alert("Customer deleted successfully.");

      await loadCustomers();
    } catch (error) {
      console.error("Unable to delete customer:", error);
      alert("Unable to delete customer.");
    }
  };

  const changeStatus = async (id, currentStatus) => {
    try {
      const customerRef = doc(db, "customers", id);

      await updateDoc(customerRef, {
        status:
          currentStatus === "Active"
            ? "Expired"
            : "Active",

        updatedAt: Date.now(),
      });

      await loadCustomers();
    } catch (error) {
      console.error(
        "Unable to change customer status:",
        error
      );

      alert("Unable to change customer status.");
    }
  };

  const renewCustomer = async (customer) => {
    try {
      const newExpiry = calculateExpiry(
        customer.plan || "1 Month"
      );

      const customerRef = doc(
        db,
        "customers",
        customer.id
      );

      await updateDoc(customerRef, {
        status: "Active",
        expiryDate: newExpiry,
        updatedAt: Date.now(),
      });

      alert("✅ Customer renewed successfully.");

      await loadCustomers();
    } catch (error) {
      console.error("Unable to renew customer:", error);
      alert("Unable to renew customer.");
    }
  };

  const daysLeft = (date) => {
    if (!date) {
      return 0;
    }

    const today = new Date();
    const expiry = new Date(`${date}T23:59:59`);

    const difference = expiry - today;

    return Math.ceil(
      difference / (1000 * 60 * 60 * 24)
    );
  };

  return (
    <>
      <Header />

      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <h2 className="fw-bold">
              Customer Management 👥
            </h2>

            <p className="text-muted">
              Add, edit, renew, search and manage all customer
              subscriptions.
            </p>
          </Col>
        </Row>

        <CustomerForm
          name={name}
          setName={setName}
          email={email}
          setEmail={setEmail}
          phone={phone}
          setPhone={setPhone}
          plan={plan}
          setPlan={setPlan}
          status={status}
          setStatus={setStatus}
          expiryDate={expiryDate}
          setExpiryDate={setExpiryDate}
          callAccess={callAccess}
          setCallAccess={setCallAccess}
          addCustomer={addCustomer}
          updateCustomer={updateCustomer}
          clearForm={clearForm}
          editingCustomerId={editingCustomerId}
        />

        <CustomerTable
          customers={customers}
          search={search}
          setSearch={setSearch}
          changeStatus={changeStatus}
          renewCustomer={renewCustomer}
          startEditCustomer={startEditCustomer}
          deleteCustomer={deleteCustomer}
          daysLeft={daysLeft}
        />
      </Container>
    </>
  );
}

export default ManageCustomers;